import { useState, useEffect } from 'react'
import { useAuth } from '../hooks/useAuth'
import { useLogto } from '@logto/react'
import LoadingSpinner from '@/components/LoadingSpinner'
import DashboardGrid from '@/components/DashboardGrid'
import DashboardPreview from '../components/DashboardPreview'
import './LandingPage.css'

// Plausible tracking helper
const trackEvent = (eventName: string, props?: Record<string, any>) => {
  if (typeof window !== 'undefined' && (window as any).plausible) {
    (window as any).plausible(eventName, { props });
  }
}

const LandingPage = () => {
  const { isAuthenticated, isLoading: authLoading, login } = useAuth()
  const { getIdTokenClaims, fetchUserInfo } = useLogto()
  const [isLoggingIn, setIsLoggingIn] = useState(false)
  const [_userClaims, setUserClaims] = useState<any>(null)
  const [_userInfo, setUserInfo] = useState<any>(null)
  const [animatedMetrics, setAnimatedMetrics] = useState({
    marbling: 5.2,
    accuracy: 98.5
  })

  // Fetch user claims and userinfo when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      // Track successful authentication
      trackEvent('Authentication Success', {
        method: 'logto',
        page: 'landing'
      });

      // Fetch ID token claims
      getIdTokenClaims()
        .then(claims => {
          setUserClaims(claims)
          trackEvent('User Claims Loaded', {
            hasRoles: !!(claims as any)?.roles,
            claimCount: Object.keys(claims || {}).length
          });
        })
        .catch(err => {
          console.error('Failed to get ID token claims:', err)
          setUserClaims(null)
          trackEvent('User Claims Error', {
            error: err.message || 'unknown'
          });
        })

      // Fetch userinfo (includes roles and other extended claims)
      fetchUserInfo()
        .then(info => {
          setUserInfo(info)
          trackEvent('User Info Loaded', {
            hasCustomData: !!(info as any)?.custom_data,
            infoFields: Object.keys(info || {}).length
          });
        })
        .catch(err => {
          console.error('Failed to get user info:', err)
          setUserInfo(null)
          trackEvent('User Info Error', {
            error: err.message || 'unknown'
          });
        })
    } else {
      setUserClaims(null)
      setUserInfo(null)
    }
  }, [isAuthenticated, getIdTokenClaims, fetchUserInfo])

  // Intersection Observer for scroll animations with tracking
  useEffect(() => {
    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible')
          
          // Track section views
          const section = entry.target.closest('section');
          if (section) {
            const sectionClass = section.className;
            let sectionName = 'Unknown Section';
            
            if (sectionClass.includes('hero-section')) sectionName = 'Hero Section';
            else if (sectionClass.includes('features-section')) sectionName = 'Features Section';
            else if (sectionClass.includes('dashboard-showcase-section')) sectionName = 'Dashboard Showcase';
            else if (sectionClass.includes('dashboard-section')) sectionName = 'Dashboard Grid';
            else if (sectionClass.includes('cta-section')) sectionName = 'CTA Section';
            
            trackEvent('Section View', {
              section: sectionName,
              authenticated: isAuthenticated,
              scrollDirection: entry.boundingClientRect.top < 0 ? 'up' : 'down'
            });
          }
        }
      })
    }

    const observer = new IntersectionObserver(observerCallback, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    })

    // Observe all elements with animate-on-scroll class
    const animatedElements = document.querySelectorAll('.animate-on-scroll')
    animatedElements.forEach((el) => observer.observe(el))

    return () => observer.disconnect()
  }, [isAuthenticated]) // Re-run when authentication state changes

  // Animate metrics for realistic demo
  useEffect(() => {
    const interval = setInterval(() => {
      setAnimatedMetrics(() => ({
        marbling: Number((5.0 + Math.random() * 0.8).toFixed(1)),
        accuracy: Number((98.2 + Math.random() * 0.8).toFixed(1))
      }))
    }, 3000)

    return () => clearInterval(interval)
  }, [])

  const handleLogin = async () => {
    try {
      setIsLoggingIn(true)
      trackEvent('Login Initiated', {
        page: 'landing',
        method: 'logto',
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString()
      });
      
      await login()
      
      trackEvent('Login Success', {
        page: 'landing',
        method: 'logto'
      });
    } catch (err) {
      console.error('Login error:', err)
      trackEvent('Login Error', {
        page: 'landing',
        method: 'logto',
        error: err instanceof Error ? err.message : 'unknown'
      });
      // Could show toast notification here
    } finally {
      setIsLoggingIn(false)
    }
  }

  const handleLearnMore = () => {
    trackEvent('Learn More Clicked', {
      page: 'landing',
      section: 'hero',
      authenticated: isAuthenticated
    });
    // Add navigation logic here if needed
  }

  const handleContactSupport = () => {
    trackEvent('Contact Support Clicked', {
      page: 'landing',
      authenticated: isAuthenticated
    });
    // Add contact logic here if needed
  }

  const handleAccountSettings = () => {
    trackEvent('Account Settings Clicked', {
      page: 'landing',
      authenticated: isAuthenticated
    });
    // Add settings navigation logic here if needed
  }

  const isLoading = authLoading || isLoggingIn

  return (
    <div className="landing-page">
      {/* Hero Section - MEQ Labs Innovation Hub only for unauthenticated users */}
      {!isAuthenticated && (
        <section className="hero-section">
          <div className="container">
            <div className="hero-content">
              <div className="hero-text">
                {/* MEQ Labs AgTech Platform */}
                <h1 className="hero-title">
                  MEQ Labs
                  <span className="hero-title-accent">Innovation Hub</span>
                </h1>
                
                <div className="hero-tagline">
                  <span className="tagline-item">Early Access Features</span>
                  <span className="tagline-item">Bespoke Data Science Projects</span>
                  <span className="tagline-item">Enterprise Partners</span>
                </div>

                <p className="hero-description">
                  Step into the future of meat science with access to MEQ Labs, our platform for pre-release MEQInsights features and Enterprise Data Science Projects.
                </p>
                
                <div className="hero-actions">
                  <button 
                    className="btn btn-large scale-on-hover"
                    onClick={handleLogin}
                    disabled={isLoading}
                    aria-label="Access MEQ Solutions Data Platform"
                  >
                    {isLoading ? <LoadingSpinner /> : 'Request Access'}
                  </button>
                  <button 
                    className="btn btn-secondary btn-large scale-on-hover"
                    onClick={handleLearnMore}
                  >
                    Learn More
                  </button>
                </div>
                
                <div className="hero-trust">
                  <p className="trust-text">Building the data-driven digital future for the global red meat industry</p>
                  <div className="trust-badges">
                    <span className="trust-badge">Objective Data</span>
                    <span className="trust-badge">Global Solutions</span>
                    <span className="trust-badge">Proven Results</span>
                  </div>
                </div>
              </div>
              <div className="hero-visual">
                <DashboardPreview animatedMetrics={animatedMetrics} />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Dashboard Selection Section - Show for authenticated users */}
      {isAuthenticated && (
        <section className="dashboard-section">
          <div className="container">
            <DashboardGrid />
          </div>
        </section>
      )}

      {/* Features Section - MEQ Solutions Focus - Only show when not authenticated */}
      {!isAuthenticated && (
        <section className="features-section" id="features">
          <div className="container">
            <div className="section-header animate-on-scroll">
              <h2 className="section-title">Data Solutions for Red Meat Industry</h2>
              <p className="section-description">
                Providing the tools to meet evolving consumer demands for Quality, Nutrition, and Sustainability 
                through objective, timely, and actionable data at the individual carcase level.
              </p>
            </div>
            
            <div className="features-grid">
              <div className="feature-card animate-on-scroll">
                <div className="feature-icon">⚡</div>
                <h3 className="feature-title">Hot & Cold Carcase Grading</h3>
                <p className="feature-description">
                  Revolutionary MEQ probe technology delivers objective grading data enabling 
                  optimisation of carcase value, chiller efficiency, and eating quality assessment 
                  to support and reinforce brand promises across the supply chain.
                </p>
                <div className="feature-stats">
                  <div className="feature-stat">
                    <div className="feature-stat-value">Individual</div>
                    <div className="feature-stat-label">Carcase Level</div>
                  </div>
                  <div className="feature-stat">
                    <div className="feature-stat-value">Real-time</div>
                    <div className="feature-stat-label">Processing</div>
                  </div>
                </div>
              </div>
              
              <div className="feature-card animate-on-scroll">
                <div className="feature-icon">📊</div>
                <h3 className="feature-title">Bespoke Data Analytics</h3>
                <p className="feature-description">
                  Modern machine learning approaches extract insights from diverse data types, 
                  enabling processors to optimize operations, manage to product specifications, 
                  and deliver transparent nutritional and sustainability data.
                </p>
                <div className="feature-stats">
                  <div className="feature-stat">
                    <div className="feature-stat-value">ML</div>
                    <div className="feature-stat-label">Algorithms</div>
                  </div>
                  <div className="feature-stat">
                    <div className="feature-stat-value">Multi-type</div>
                    <div className="feature-stat-label">Data Sources</div>
                  </div>
                </div>
              </div>
              
              <div className="feature-card animate-on-scroll">
                <div className="feature-icon">🌱</div>
                <h3 className="feature-title">Environmental Impact Optimization</h3>
                <p className="feature-description">
                  Know the minimal environmental impact for the greatest outcome. MEQ enables 
                  substantial positive environment impact whilst driving better product margins 
                  through data-driven genetics and breeding performance acceleration.
                </p>
                <div className="feature-stats">
                  <div className="feature-stat">
                    <div className="feature-stat-value">Minimal</div>
                    <div className="feature-stat-label">Impact</div>
                  </div>
                  <div className="feature-stat">
                    <div className="feature-stat-value">Maximum</div>
                    <div className="feature-stat-label">Outcome</div>
                  </div>
                </div>
              </div>
              
              <div className="feature-card animate-on-scroll">
                <div className="feature-icon">🎯</div>
                <h3 className="feature-title">Live Animal Assessment</h3>
                <p className="feature-description">
                  MEQ Live delivers real-time livestock asset value assessment, optimized days 
                  on feed management, and accelerated genetics performance - managing to product 
                  specification rather than time-based feeding protocols.
                </p>
                <div className="feature-stats">
                  <div className="feature-stat">
                    <div className="feature-stat-value">Live</div>
                    <div className="feature-stat-label">Assessment</div>
                  </div>
                  <div className="feature-stat">
                    <div className="feature-stat-value">Optimized</div>
                    <div className="feature-stat-label">Feed Days</div>
                  </div>
                </div>
              </div>
              
              <div className="feature-card animate-on-scroll">
                <div className="feature-icon">📱</div>
                <h3 className="feature-title">Novel Hardware Solutions</h3>
                <p className="feature-description">
                  Innovative imaging and intrusive measurement technologies harvest MEQ and 
                  third-party data, providing unprecedented transparency and traceability 
                  from feedlot to retail with cut-by-cut nutritional information.
                </p>
                <div className="feature-stats">
                  <div className="feature-stat">
                    <div className="feature-stat-value">Novel</div>
                    <div className="feature-stat-label">Hardware</div>
                  </div>
                  <div className="feature-stat">
                    <div className="feature-stat-value">Full</div>
                    <div className="feature-stat-label">Traceability</div>
                  </div>
                </div>
              </div>
              
              <div className="feature-card animate-on-scroll">
                <div className="feature-icon">🏆</div>
                <h3 className="feature-title">Industry Leadership</h3>
                <p className="feature-description">
                  Identify the world's best genetics and practitioners through objective data 
                  analysis. MEQ provides the red meat industry with tools to meet evolving 
                  consumer demands for quality, nutrition, and sustainability transparency.
                </p>
                <div className="feature-stats">
                  <div className="feature-stat">
                    <div className="feature-stat-value">Global</div>
                    <div className="feature-stat-label">Best Practice</div>
                  </div>
                  <div className="feature-stat">
                    <div className="feature-stat-value">World-class</div>
                    <div className="feature-stat-label">Genetics</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Features Section - Enterprise Analytics Features - Only show when authenticated */}
      {isAuthenticated && (
      <section className="features-section" id="features">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Powerful Analytics Features</h2>
            <p className="section-description">
              Everything you need to transform your data into actionable insights
            </p>
          </div>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">📊</div>
              <h3 className="feature-title">Interactive Dashboards</h3>
              <p className="feature-description">
                Create and customize interactive dashboards with real-time data visualization and filtering capabilities.
              </p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🔒</div>
              <h3 className="feature-title">Enterprise Security</h3>
              <p className="feature-description">
                Role-based access control, SSO integration, and enterprise-grade security for your sensitive data.
              </p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">⚡</div>
              <h3 className="feature-title">Real-time Analytics</h3>
              <p className="feature-description">
                Get instant insights with real-time data processing and automated report generation.
              </p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🔗</div>
              <h3 className="feature-title">Data Integration</h3>
              <p className="feature-description">
                Connect to multiple data sources including databases, APIs, and cloud platforms seamlessly.
              </p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">📱</div>
              <h3 className="feature-title">Mobile Responsive</h3>
              <p className="feature-description">
                Access your dashboards on any device with our fully responsive design and mobile app.
              </p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🎯</div>
              <h3 className="feature-title">Smart Insights</h3>
              <p className="feature-description">
                AI-powered analytics that automatically discover patterns and provide actionable recommendations.
              </p>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Dashboard Showcase Section - Only show when not authenticated */}
      {!isAuthenticated && (
        <section className="dashboard-showcase-section">
          <div className="container">
            <div className="section-header animate-on-scroll">
              <h2 className="section-title">Dashboard Intelligence Platform</h2>
              <p className="section-description">
                Experience the power of MEQ's data visualization platform with industry-leading dashboard capabilities 
                designed specifically for meat science and processing operations.
              </p>
            </div>
            
            <div className="showcase-grid">
              {/* Carcase Analytics Dashboard */}
              <div className="showcase-item animate-on-scroll">
                <div className="showcase-header">
                  <div className="showcase-title">
                    <span className="showcase-icon">🥩</span>
                    <h3>Carcase Analytics Dashboard</h3>
                  </div>
                  <div className="showcase-status">
                    <span className="status-dot live"></span>
                    <span className="status-text">Live Data</span>
                  </div>
                </div>
                <div className="showcase-preview">
                  <div className="mini-dashboard carcase-dashboard">
                    <div className="dashboard-metrics">
                      <div className="mini-metric">
                        <span className="mini-value">MB {animatedMetrics.marbling}</span>
                        <span className="mini-label">Avg Marbling</span>
                      </div>
                      <div className="mini-metric">
                        <span className="mini-value">{animatedMetrics.accuracy}%</span>
                        <span className="mini-label">Accuracy</span>
                      </div>
                      <div className="mini-metric">
                        <span className="mini-value">1,247</span>
                        <span className="mini-label">Processed</span>
                      </div>
                    </div>
                    <div className="mini-chart">
                      <div className="chart-bars mini">
                        <div className="bar" style={{ height: '70%' }}></div>
                        <div className="bar" style={{ height: '85%' }}></div>
                        <div className="bar" style={{ height: '60%' }}></div>
                        <div className="bar" style={{ height: '95%' }}></div>
                        <div className="bar" style={{ height: '75%' }}></div>
                        <div className="bar" style={{ height: '80%' }}></div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="showcase-features">
                  <span className="feature-tag">Real-time Grading</span>
                  <span className="feature-tag">Quality Assessment</span>
                  <span className="feature-tag">Yield Optimization</span>
                </div>
              </div>

              {/* Sustainability Dashboard */}
              <div className="showcase-item animate-on-scroll">
                <div className="showcase-header">
                  <div className="showcase-title">
                    <span className="showcase-icon">🌱</span>
                    <h3>Sustainability Metrics</h3>
                  </div>
                  <div className="showcase-status">
                    <span className="status-dot processing"></span>
                    <span className="status-text">Processing</span>
                  </div>
                </div>
                <div className="showcase-preview">
                  <div className="mini-dashboard sustainability-dashboard">
                    <div className="dashboard-metrics">
                      <div className="mini-metric">
                        <span className="mini-value">-12%</span>
                        <span className="mini-label">Carbon Reduction</span>
                      </div>
                      <div className="mini-metric">
                        <span className="mini-value">89%</span>
                        <span className="mini-label">Efficiency</span>
                      </div>
                    </div>
                    <div className="sustainability-rings">
                      <div className="ring-chart">
                        <div className="ring" style={{ '--percentage': '75' } as React.CSSProperties}></div>
                        <span className="ring-label">Carbon Impact</span>
                      </div>
                      <div className="ring-chart">
                        <div className="ring" style={{ '--percentage': '89' } as React.CSSProperties}></div>
                        <span className="ring-label">Resource Efficiency</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="showcase-features">
                  <span className="feature-tag">Carbon Tracking</span>
                  <span className="feature-tag">Resource Management</span>
                  <span className="feature-tag">Impact Analysis</span>
                </div>
              </div>

              {/* Production Analytics */}
              <div className="showcase-item animate-on-scroll">
                <div className="showcase-header">
                  <div className="showcase-title">
                    <span className="showcase-icon">📈</span>
                    <h3>Production Analytics</h3>
                  </div>
                  <div className="showcase-status">
                    <span className="status-dot active"></span>
                    <span className="status-text">Active</span>
                  </div>
                </div>
                <div className="showcase-preview">
                  <div className="mini-dashboard production-dashboard">
                    <div className="dashboard-metrics">
                      <div className="mini-metric">
                        <span className="mini-value">847</span>
                        <span className="mini-label">Daily Output</span>
                      </div>
                      <div className="mini-metric">
                        <span className="mini-value">96%</span>
                        <span className="mini-label">Throughput</span>
                      </div>
                    </div>
                    <div className="production-flow">
                      <div className="flow-stage">
                        <div className="stage-indicator active"></div>
                        <span className="stage-label">Intake</span>
                      </div>
                      <div className="flow-connector"></div>
                      <div className="flow-stage">
                        <div className="stage-indicator active"></div>
                        <span className="stage-label">Processing</span>
                      </div>
                      <div className="flow-connector"></div>
                      <div className="flow-stage">
                        <div className="stage-indicator processing"></div>
                        <span className="stage-label">Grading</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="showcase-features">
                  <span className="feature-tag">Throughput Monitoring</span>
                  <span className="feature-tag">Process Optimization</span>
                  <span className="feature-tag">Quality Control</span>
                </div>
              </div>

              {/* Genetics Dashboard */}
              <div className="showcase-item animate-on-scroll">
                <div className="showcase-header">
                  <div className="showcase-title">
                    <span className="showcase-icon">🧬</span>
                    <h3>Genetics Performance</h3>
                  </div>
                  <div className="showcase-status">
                    <span className="status-dot live"></span>
                    <span className="status-text">Live Analysis</span>
                  </div>
                </div>
                <div className="showcase-preview">
                  <div className="mini-dashboard genetics-dashboard">
                    <div className="dashboard-metrics">
                      <div className="mini-metric">
                        <span className="mini-value">A++</span>
                        <span className="mini-label">Grade Score</span>
                      </div>
                      <div className="mini-metric">
                        <span className="mini-value">+23%</span>
                        <span className="mini-label">Performance</span>
                      </div>
                    </div>
                    <div className="genetics-grid">
                      <div className="gene-indicator high"></div>
                      <div className="gene-indicator medium"></div>
                      <div className="gene-indicator high"></div>
                      <div className="gene-indicator low"></div>
                      <div className="gene-indicator high"></div>
                      <div className="gene-indicator medium"></div>
                    </div>
                  </div>
                </div>
                <div className="showcase-features">
                  <span className="feature-tag">Breeding Analysis</span>
                  <span className="feature-tag">Performance Tracking</span>
                  <span className="feature-tag">Genetic Optimization</span>
                </div>
              </div>
            </div>
            
            <div className="showcase-cta animate-on-scroll">
              <div className="showcase-cta-content">
                <h3 className="showcase-cta-title">Unlock the Full Dashboard Experience</h3>
                <p className="showcase-cta-description">
                  Get access to all dashboard capabilities, real-time data, and advanced analytics features.
                </p>
                <button 
                  className="btn btn-primary btn-large scale-on-hover"
                  onClick={handleLogin}
                  disabled={isLoading}
                >
                  {isLoading ? <LoadingSpinner /> : 'Access Dashboards'}
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section - Enhanced - Only show when not authenticated */}
      {!isAuthenticated && (
        <section className="cta-section">
          <div className="container">
            <div className="cta-content animate-on-scroll">
              <h2 className="cta-title">Delivering the Data-Driven Future</h2>
              <p className="cta-description">
                The global $700B red meat industry lacks timely, objective and actionable data. 
                MEQ gives the industry the tools to revolutionize efficiency, quality, nutritional value, and environmental impact.
              </p>
              <div className="cta-actions">
                <button 
                  className="btn btn-primary btn-large scale-on-hover"
                  onClick={handleLogin}
                  disabled={isLoading}
                >
                  {isLoading ? <LoadingSpinner /> : 'Request a Demo'}
                </button>
                <button 
                  className="btn btn-outline btn-large scale-on-hover"
                  onClick={handleLearnMore}
                >
                  Learn About MEQ
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section - Enterprise - Only show when authenticated */}
      {isAuthenticated && (
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">Ready to Get Started?</h2>
            <p className="cta-description">
              Join thousands of organizations already using MEQ Insights to make data-driven decisions.
            </p>
            <div className="cta-actions">
                  <button 
                    className="btn btn-outline btn-large"
                    onClick={handleAccountSettings}
                  >
                    Account Settings
                  </button>
                  <button 
                    className="btn btn-outline btn-large"
                    onClick={handleContactSupport}
                  >
                    Contact Support
                  </button>
              </div>
          </div>
        </div>
      </section>
      )}
    </div>
  )
}

export default LandingPage 